import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Euro, Save, Star, Users, Video, TrendingUp, FileText, ShieldCheck, RotateCcw, CreditCard, Tag, Gift, Percent, Coins, CalendarDays, FileEdit, Trophy, Trash2, Heart, Zap, Award, Target, Sparkles, ThumbsUp, Smile, Coffee, Lightbulb, Music, Mic, BookOpen, MessageCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface RewardType {
  id: string;
  name: string;
  description: string;
  amount: number;
  icon: string;
  color: string;
  type?: string;
  auto_approve?: boolean;
  max_times: number;
  reward_method?: string;
  // REVIEW only: where the vendor collects reviews + the G2 link and (separate) G2 amount.
  review_platform?: string;
  g2_review_url?: string | null;
  g2_amount?: number | null;
  reward_policy_trigger?: string;
  lead_submission_amount?: number | null;
  lead_submission_reward_method?: string | null;
  lead_submission_discount_type?: string | null;
  lead_submission_discount_value?: number | null;
  lead_submission_discount_product_name?: string | null;
  lead_submission_discount_name?: string | null;
  deal_won_amount?: number | null;
  deal_won_reward_method?: string | null;
  deal_won_discount_type?: string | null;
  deal_won_discount_value?: number | null;
  deal_won_discount_product_name?: string | null;
  deal_won_discount_name?: string | null;
  discount_type?: string | null;
  discount_value?: number | null;
  discount_product_name?: string | null;
  discount_name?: string | null;
}

interface VendorProduct {
  name: string;
  description: string;
}

interface EditRewardModalProps {
  rewardType: RewardType | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (rewardType: RewardType) => void | Promise<void>;
  onDelete?: (rewardId: string) => void | Promise<void>;
  currencySymbol?: string;
  currency?: string;
  products?: VendorProduct[];
}

interface StageRewardState {
  amount: number | null;
  reward_method: string;
  discount_type: string;
  discount_value: number | null;
  discount_product_name: string;
}

function buildDiscountName(discountType: string, discountValue: number | null, productName: string): string | null {
  if (!discountValue) return null;
  const productSuffix = productName ? ` on ${productName}` : '';
  if (discountType === 'percentage') return `${discountValue}% off${productSuffix}`;
  if (discountType === 'credit_points') return `${discountValue} credit points${productSuffix}`;
  return `${discountValue} free month${discountValue !== 1 ? 's' : ''}${productSuffix}`;
}

/** Inline reward method + amount + discount config for a single stage */
function StageRewardConfig({
  state,
  onChange,
  currencySymbol,
  currency,
  products,
  accentColor,
}: {
  state: StageRewardState;
  onChange: (patch: Partial<StageRewardState>) => void;
  currencySymbol: string;
  currency: string;
  products: VendorProduct[];
  accentColor: 'blue' | 'emerald';
}) {
  const { t } = useTranslation();
  const showGiftCard = state.reward_method === 'GIFT_CARD' || state.reward_method === 'BOTH';
  const showDiscount = state.reward_method === 'DISCOUNT' || state.reward_method === 'BOTH';
  const ring = accentColor === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-emerald-500';

  return (
    <div className="space-y-3">
      {/* Reward Method selector */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { value: 'GIFT_CARD', label: t('rewards.create.methodGiftCard'), icon: CreditCard },
          { value: 'DISCOUNT', label: t('rewards.create.methodDiscount'), icon: Tag },
          { value: 'BOTH', label: t('rewards.create.methodBoth'), icon: Gift },
        ].map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => onChange({ reward_method: value })}
            className={`p-2 rounded-lg border-2 text-center transition-all text-xs ${
              state.reward_method === value
                ? accentColor === 'blue'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Icon className={`w-4 h-4 mx-auto mb-0.5 ${
              state.reward_method === value
                ? accentColor === 'blue' ? 'text-blue-600' : 'text-emerald-600'
                : 'text-gray-400'
            }`} />
            <div className={`font-medium ${
              state.reward_method === value
                ? accentColor === 'blue' ? 'text-blue-700' : 'text-emerald-700'
                : 'text-gray-700'
            }`}>{label}</div>
          </button>
        ))}
      </div>

      {/* Gift Card Amount */}
      {showGiftCard && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            {state.reward_method === 'BOTH' ? t('rewards.create.giftCardAmountBoth') : t('rewards.create.amount')} ({currency})
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
              {currencySymbol}
            </span>
            <input
              type="number"
              value={state.amount ?? ''}
              onChange={(e) => onChange({ amount: e.target.value === '' ? null : parseInt(e.target.value) || 0 })}
              className={`w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg ${ring} focus:ring-2 focus:border-transparent text-sm`}
              placeholder="0"
              min="0"
            />
          </div>
        </div>
      )}

      {/* Discount config */}
      {showDiscount && (
        <div className={`space-y-2.5 p-3 rounded-lg border ${
          accentColor === 'blue' ? 'bg-blue-50/50 border-blue-200' : 'bg-emerald-50/50 border-emerald-200'
        }`}>
          <h5 className="text-xs font-medium text-gray-700">{t('rewards.create.discountConfig')}</h5>

          {/* Discount type */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {[
              { value: 'percentage', label: 'Percentage', icon: Percent },
              { value: 'credit_points', label: 'Credits', icon: Coins },
              { value: 'free_months', label: 'Free Mo.', icon: CalendarDays },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  onChange({ discount_type: value });
                  if (value === 'free_months' && state.discount_value) {
                    onChange({ discount_type: value, discount_value: Math.max(1, Math.floor(state.discount_value)) });
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 text-[11px] font-medium transition-colors ${
                  state.discount_type === value
                    ? accentColor === 'blue' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${value !== 'percentage' ? 'border-l border-gray-300' : ''}`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>

          {/* Discount value */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {state.discount_type === 'percentage' ? 'Discount Value' : state.discount_type === 'credit_points' ? 'Credit Points' : 'Number of Months'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">
                {state.discount_type === 'percentage' ? '%' : state.discount_type === 'credit_points' ? 'pts' : 'mo'}
              </span>
              <input
                type="number"
                value={state.discount_value ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    onChange({ discount_value: null });
                  } else {
                    const num = state.discount_type === 'free_months'
                      ? Math.max(0, Math.floor(parseFloat(val) || 0))
                      : parseFloat(val);
                    onChange({ discount_value: num });
                  }
                }}
                className={`w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg ${ring} focus:ring-2 focus:border-transparent text-sm`}
                placeholder="0"
                min="0"
                max={state.discount_type === 'percentage' ? 100 : undefined}
                step={state.discount_type === 'free_months' ? 1 : undefined}
              />
            </div>
          </div>

          {/* Product selector */}
          {products.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Product (optional)</label>
              <select
                value={state.discount_product_name}
                onChange={(e) => onChange({ discount_product_name: e.target.value })}
                className={`w-full p-2 border border-gray-300 rounded-lg ${ring} focus:ring-2 focus:border-transparent text-sm`}
              >
                <option value="">No specific product</option>
                {products.map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Preview */}
          {state.discount_value && (
            <div className={`bg-white rounded-lg p-2 border text-xs font-medium ${
              accentColor === 'blue' ? 'border-blue-200 text-blue-800' : 'border-emerald-200 text-emerald-800'
            }`}>
              Preview: {buildDiscountName(state.discount_type, state.discount_value, state.discount_product_name)}
            </div>
          )}
        </div>
      )}

      {state.reward_method === 'BOTH' && (
        <p className="text-[11px] text-gray-500">
          Promoters will choose between gift card or discount.
        </p>
      )}
    </div>
  );
}

const CUSTOM_ICON_OPTIONS: { name: string; icon: LucideIcon }[] = [
  { name: 'star', icon: Star },
  { name: 'heart', icon: Heart },
  { name: 'zap', icon: Zap },
  { name: 'award', icon: Award },
  { name: 'gift', icon: Gift },
  { name: 'target', icon: Target },
  { name: 'sparkles', icon: Sparkles },
  { name: 'trophy', icon: Trophy },
  { name: 'thumbsup', icon: ThumbsUp },
  { name: 'smile', icon: Smile },
  { name: 'coffee', icon: Coffee },
  { name: 'lightbulb', icon: Lightbulb },
  { name: 'music', icon: Music },
  { name: 'mic', icon: Mic },
  { name: 'bookopen', icon: BookOpen },
  { name: 'messagecircle', icon: MessageCircle },
];

const CUSTOM_COLOR_OPTIONS = [
  'bg-blue-500', 'bg-purple-500', 'bg-red-500', 'bg-green-500', 'bg-orange-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-indigo-500', 'bg-teal-500',
];

const EditRewardModal: React.FC<EditRewardModalProps> = ({ rewardType, isOpen, onClose, onSave, onDelete, currencySymbol = '€', currency = 'EUR', products = [] }) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: 0,
    auto_approve: false,
    max_times: 1,
    reward_method: 'GIFT_CARD',
    review_platform: 'CLAREEFAI' as string,
    g2_review_url: '' as string,
    g2_amount: null as number | null,
    reward_policy_trigger: 'LEAD_SUBMISSION',
    discount_type: 'percentage' as string,
    discount_value: null as number | null,
    discount_product_name: '' as string,
  });
  const [leadStage, setLeadStage] = useState<StageRewardState>({
    amount: null,
    reward_method: 'GIFT_CARD',
    discount_type: 'percentage',
    discount_value: null,
    discount_product_name: '',
  });
  const [wonStage, setWonStage] = useState<StageRewardState>({
    amount: null,
    reward_method: 'GIFT_CARD',
    discount_type: 'percentage',
    discount_value: null,
    discount_product_name: '',
  });
  const [customIcon, setCustomIcon] = useState('star');
  const [customColor, setCustomColor] = useState('bg-purple-500');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isReferralType = rewardType?.type === 'REFERRAL';
  const isCustomType = rewardType?.type === 'CUSTOM';
  const isReviewType = rewardType?.type === 'REVIEW';

  useEffect(() => {
    if (rewardType) {
      setFormData({
        name: rewardType.name,
        description: rewardType.description,
        amount: rewardType.amount,
        auto_approve: rewardType.auto_approve ?? false,
        max_times: rewardType.max_times ?? 1,
        reward_method: rewardType.reward_method || 'GIFT_CARD',
        review_platform: rewardType.review_platform || 'CLAREEFAI',
        g2_review_url: rewardType.g2_review_url || '',
        g2_amount: rewardType.g2_amount ?? null,
        reward_policy_trigger: rewardType.reward_policy_trigger || 'LEAD_SUBMISSION',
        discount_type: rewardType.discount_type || 'percentage',
        discount_value: rewardType.discount_value ?? null,
        discount_product_name: rewardType.discount_product_name || '',
      });
      setCustomIcon(rewardType.icon || 'star');
      setCustomColor(rewardType.color || 'bg-purple-500');
      setLeadStage({
        amount: rewardType.lead_submission_amount ?? null,
        reward_method: rewardType.lead_submission_reward_method || 'GIFT_CARD',
        discount_type: rewardType.lead_submission_discount_type || 'percentage',
        discount_value: rewardType.lead_submission_discount_value ?? null,
        discount_product_name: rewardType.lead_submission_discount_product_name || '',
      });
      setWonStage({
        amount: rewardType.deal_won_amount ?? null,
        reward_method: rewardType.deal_won_reward_method || 'GIFT_CARD',
        discount_type: rewardType.deal_won_discount_type || 'percentage',
        discount_value: rewardType.deal_won_discount_value ?? null,
        discount_product_name: rewardType.deal_won_discount_product_name || '',
      });
    }
  }, [rewardType]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        document.body.style.overflow = 'unset';
      }, 300);
      return () => clearTimeout(timer);
    }

    return () => {
      if (!isOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleChange = (field: string, value: string | number | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isBothTrigger = isReferralType && formData.reward_policy_trigger === 'BOTH';

  const getPreviewAmount = () => {
    if (isBothTrigger) {
      return (leadStage.amount || 0) + (wonStage.amount || 0);
    }
    return formData.amount;
  };

  const getPreviewBreakdown = () => {
    if (isBothTrigger) {
      return `${leadStage.amount || 0} ${currency} for lead + ${wonStage.amount || 0} ${currency} when deal closes`;
    }
    return null;
  };

  const handleSave = async () => {
    if (!rewardType) return;
    setIsSaving(true);

    try {
      const computedAmount = isBothTrigger
        ? (leadStage.amount || 0) + (wonStage.amount || 0)
        : formData.amount;

      const updatedReward: RewardType = {
        ...rewardType,
        name: formData.name,
        description: formData.description,
        amount: computedAmount,
        icon: isCustomType ? customIcon : rewardType.icon,
        color: isCustomType ? customColor : rewardType.color,
        auto_approve: formData.auto_approve,
        max_times: formData.max_times,
        reward_method: isBothTrigger ? rewardType.reward_method : formData.reward_method,
        // REVIEW only: persist where reviews are collected, the G2 link, and the separate G2 amount.
        review_platform: isReviewType ? formData.review_platform : rewardType.review_platform,
        g2_review_url: isReviewType && formData.review_platform !== 'CLAREEFAI'
          ? (formData.g2_review_url?.trim() || null)
          : null,
        g2_amount: isReviewType && formData.review_platform !== 'CLAREEFAI'
          ? formData.g2_amount
          : null,
        reward_policy_trigger: isReferralType ? formData.reward_policy_trigger : rewardType.reward_policy_trigger,
        // Lead stage fields
        lead_submission_amount: isBothTrigger ? leadStage.amount : null,
        lead_submission_reward_method: isBothTrigger ? leadStage.reward_method : null,
        lead_submission_discount_type: isBothTrigger && (leadStage.reward_method === 'DISCOUNT' || leadStage.reward_method === 'BOTH') ? leadStage.discount_type : null,
        lead_submission_discount_value: isBothTrigger && (leadStage.reward_method === 'DISCOUNT' || leadStage.reward_method === 'BOTH') ? leadStage.discount_value : null,
        lead_submission_discount_product_name: isBothTrigger && (leadStage.reward_method === 'DISCOUNT' || leadStage.reward_method === 'BOTH') ? (leadStage.discount_product_name?.trim() || null) : null,
        lead_submission_discount_name: isBothTrigger && (leadStage.reward_method === 'DISCOUNT' || leadStage.reward_method === 'BOTH') ? buildDiscountName(leadStage.discount_type, leadStage.discount_value, leadStage.discount_product_name) : null,
        // Won stage fields
        deal_won_amount: isBothTrigger ? wonStage.amount : null,
        deal_won_reward_method: isBothTrigger ? wonStage.reward_method : null,
        deal_won_discount_type: isBothTrigger && (wonStage.reward_method === 'DISCOUNT' || wonStage.reward_method === 'BOTH') ? wonStage.discount_type : null,
        deal_won_discount_value: isBothTrigger && (wonStage.reward_method === 'DISCOUNT' || wonStage.reward_method === 'BOTH') ? wonStage.discount_value : null,
        deal_won_discount_product_name: isBothTrigger && (wonStage.reward_method === 'DISCOUNT' || wonStage.reward_method === 'BOTH') ? (wonStage.discount_product_name?.trim() || null) : null,
        deal_won_discount_name: isBothTrigger && (wonStage.reward_method === 'DISCOUNT' || wonStage.reward_method === 'BOTH') ? buildDiscountName(wonStage.discount_type, wonStage.discount_value, wonStage.discount_product_name) : null,
        // Global discount fields (non-BOTH)
        discount_type: !isBothTrigger && showDiscount ? formData.discount_type : rewardType.discount_type,
        discount_value: !isBothTrigger && showDiscount ? formData.discount_value : rewardType.discount_value,
        discount_product_name: !isBothTrigger && showDiscount ? (formData.discount_product_name?.trim() || null) : rewardType.discount_product_name,
        discount_name: !isBothTrigger && showDiscount
          ? buildDiscountName(formData.discount_type, formData.discount_value, formData.discount_product_name)
          : rewardType.discount_name,
      };

      await onSave(updatedReward);
      handleClose();
    } catch (error) {
      console.error('Failed to save reward:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'star': return <Star className="w-8 h-8" />;
      case 'users': return <Users className="w-8 h-8" />;
      case 'video': return <Video className="w-8 h-8" />;
      case 'trending': return <TrendingUp className="w-8 h-8" />;
      case 'file': return <FileText className="w-8 h-8" />;
      case 'heart': return <Heart className="w-8 h-8" />;
      case 'zap': return <Zap className="w-8 h-8" />;
      case 'award': return <Award className="w-8 h-8" />;
      case 'gift': return <Gift className="w-8 h-8" />;
      case 'target': return <Target className="w-8 h-8" />;
      case 'sparkles': return <Sparkles className="w-8 h-8" />;
      case 'trophy': return <Trophy className="w-8 h-8" />;
      case 'thumbsup': return <ThumbsUp className="w-8 h-8" />;
      case 'smile': return <Smile className="w-8 h-8" />;
      case 'coffee': return <Coffee className="w-8 h-8" />;
      case 'lightbulb': return <Lightbulb className="w-8 h-8" />;
      case 'music': return <Music className="w-8 h-8" />;
      case 'mic': return <Mic className="w-8 h-8" />;
      case 'bookopen': return <BookOpen className="w-8 h-8" />;
      case 'messagecircle': return <MessageCircle className="w-8 h-8" />;
      default: return <Star className="w-8 h-8" />;
    }
  };

  if (!shouldRender || !rewardType) return null;

  const showGiftCard = formData.reward_method === 'GIFT_CARD' || formData.reward_method === 'BOTH';
  const showDiscount = formData.reward_method === 'DISCOUNT' || formData.reward_method === 'BOTH';

  const REWARD_POLICY_OPTIONS = [
    {
      value: 'LEAD_SUBMISSION',
      label: t('rewards.edit.policyLeadLabel'),
      description: t('rewards.edit.policyLeadDesc'),
    },
    {
      value: 'DEAL_WON',
      label: t('rewards.edit.policyDealWonLabel'),
      description: t('rewards.edit.policyDealWonDesc'),
    },
    {
      value: 'BOTH',
      label: t('rewards.edit.policyBothLabel'),
      description: t('rewards.edit.policyBothDesc'),
    },
  ];

  return (
    <>
      <div
        className={`modal-overlay ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`}
        onClick={handleOverlayClick}
      />
      <div className={`fixed top-0 right-0 h-full w-[500px] max-w-[90vw] bg-white shadow-2xl z-[2000] transform transition-transform duration-300 ease-in-out ${
        isOpen && !isClosing ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <h2 className="text-xl font-semibold">{t('rewards.edit.title')}</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-gray-50 to-white" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
          <div className="space-y-6">
            {/* Reward Type Preview */}
            <div className="bg-orange-50 rounded-xl border border-orange-200/30 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('rewards.edit.typePreview')}</h3>
              <div className="flex items-start space-x-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isCustomType ? customColor : rewardType.color}`}>
                  {getIcon(isCustomType ? customIcon : rewardType.icon)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-[15px]">{isCustomType ? (formData.name || rewardType.name) : t(`rewards.types.${rewardType.type}.name`, { defaultValue: rewardType.name })}</h4>
                  <p className="text-sm text-gray-500 mt-0.5">{isCustomType ? (formData.description || rewardType.description) : t(`rewards.types.${rewardType.type}.description`, { defaultValue: rewardType.description })}</p>
                  <p className="text-2xl font-bold text-emerald-500 mt-1">{getPreviewAmount()}</p>
                  {getPreviewBreakdown() && (
                    <p className="text-xs text-gray-900 mt-0.5">{getPreviewBreakdown()}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Edit Form — name/description are editable only for custom rewards;
                predefined reward types use static, translatable name/description. */}
            {isCustomType && (
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-3">{t('rewards.edit.editConfig')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.rewardName')}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('rewards.edit.rewardNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      placeholder={t('rewards.edit.descriptionPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Review platform — REVIEW type only: where the vendor wants reviews collected */}
            {isReviewType && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  {t('rewards.edit.reviewPlatformLabel')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'CLAREEFAI', label: t('rewards.edit.reviewPlatformClareefai'), icon: Star },
                    { value: 'G2', label: t('rewards.edit.reviewPlatformG2'), icon: Award },
                    { value: 'BOTH', label: t('rewards.edit.reviewPlatformBoth'), icon: Gift },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleChange('review_platform', value)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.review_platform === value
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${formData.review_platform === value ? 'text-orange-600' : 'text-gray-400'}`} />
                      <div className={`text-sm font-medium ${formData.review_platform === value ? 'text-orange-700' : 'text-gray-700'}`}>{label}</div>
                    </button>
                  ))}
                </div>

                {/* G2 review link + separate G2 amount — shown when G2 reviews are collected */}
                {formData.review_platform !== 'CLAREEFAI' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('rewards.edit.g2ReviewUrlLabel')}
                      </label>
                      <input
                        type="url"
                        value={formData.g2_review_url}
                        onChange={(e) => handleChange('g2_review_url', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        placeholder={t('rewards.edit.g2ReviewUrlPlaceholder')}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('rewards.edit.g2ReviewUrlHelp')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('rewards.edit.g2AmountLabel')} ({currency})
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">
                          {currencySymbol}
                        </span>
                        <input
                          type="number"
                          value={formData.g2_amount ?? ''}
                          onChange={(e) => handleChange('g2_amount', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="0"
                          min="0"
                          step="5"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Icon & Color selectors — only for custom type */}
            {isCustomType && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.iconLabel')}</label>
                  <div className="grid grid-cols-8 gap-2">
                    {CUSTOM_ICON_OPTIONS.map(({ name: iconName, icon: Icon }) => (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => setCustomIcon(iconName)}
                        className={`p-2 rounded-lg border-2 transition-all flex items-center justify-center ${
                          customIcon === iconName
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${customIcon === iconName ? 'text-purple-600' : 'text-gray-500'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.colorLabel')}</label>
                  <div className="flex flex-wrap gap-2">
                    {CUSTOM_COLOR_OPTIONS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setCustomColor(color)}
                        className={`w-8 h-8 rounded-full ${color} transition-all ${
                          customColor === color
                            ? 'ring-2 ring-offset-2 ring-purple-500 scale-110'
                            : 'hover:scale-105'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Reward Policy Trigger — only for referral type */}
            {isReferralType && (
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-3">{t('rewards.edit.whenReward')}</h3>
                <div className="space-y-2">
                  {REWARD_POLICY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.reward_policy_trigger === option.value
                          ? 'border-purple-500 bg-purple-50/50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reward_policy_trigger"
                        value={option.value}
                        checked={formData.reward_policy_trigger === option.value}
                        onChange={(e) => handleChange('reward_policy_trigger', e.target.value)}
                        className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                      />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Split reward config for BOTH trigger */}
            {isBothTrigger && (
              <div className="space-y-3">
                {/* Lead Submission Reward */}
                <div className="bg-blue-50/60 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <FileEdit className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-[15px] font-semibold text-gray-900">{t('rewards.edit.rewardForLead')}</h4>
                  </div>
                  <StageRewardConfig
                    state={leadStage}
                    onChange={(patch) => setLeadStage(prev => ({ ...prev, ...patch }))}
                    currencySymbol={currencySymbol}
                    currency={currency}
                    products={products}
                    accentColor="blue"
                  />
                </div>

                {/* Deal Won Reward */}
                <div className="bg-emerald-50/60 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-emerald-500/30 rounded-full flex items-center justify-center">
                      <Trophy className="w-4 h-4 text-emerald-700" />
                    </div>
                    <h4 className="text-[15px] font-semibold text-gray-900">{t('rewards.edit.rewardForDeal')}</h4>
                  </div>
                  <StageRewardConfig
                    state={wonStage}
                    onChange={(patch) => setWonStage(prev => ({ ...prev, ...patch }))}
                    currencySymbol={currencySymbol}
                    currency={currency}
                    products={products}
                    accentColor="emerald"
                  />
                </div>
              </div>
            )}

            {/* Reward Method — for non-BOTH trigger */}
            {!isBothTrigger && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Gift className="w-5 h-5 mr-2 text-purple-600" />
                  {t('rewards.create.rewardMethod')}
                </h3>

                <div className="grid grid-cols-3 gap-2 mb-5">
                  {[
                    { value: 'GIFT_CARD', label: t('rewards.create.methodGiftCard'), icon: CreditCard, desc: t('rewards.create.methodGiftCardDesc') },
                    { value: 'DISCOUNT', label: t('rewards.create.methodDiscount'), icon: Tag, desc: t('rewards.create.methodDiscountDesc') },
                    { value: 'BOTH', label: t('rewards.create.methodBoth'), icon: Gift, desc: t('rewards.create.methodBothDesc') },
                  ].map(({ value, label, icon: Icon, desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleChange('reward_method', value)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.reward_method === value
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${formData.reward_method === value ? 'text-orange-600' : 'text-gray-400'}`} />
                      <div className={`text-sm font-medium ${formData.reward_method === value ? 'text-orange-700' : 'text-gray-700'}`}>{label}</div>
                      <div className="text-xs text-gray-500">{desc}</div>
                    </button>
                  ))}
                </div>

                {/* Gift Card Amount */}
                {showGiftCard && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('rewards.create.giftCardAmountBoth')} ({currency})
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">
                        {currencySymbol}
                      </span>
                      <input
                        type="number"
                        value={formData.amount || ''}
                        onChange={(e) => handleChange('amount', parseInt(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="0"
                        min="0"
                        step="10"
                      />
                    </div>
                  </div>
                )}

                {/* Discount Configuration */}
                {showDiscount && (
                  <div className="space-y-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="text-sm font-medium text-orange-900">{t('rewards.create.discountConfig')}</h4>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('rewards.create.discountType')}</label>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        {[
                          { value: 'percentage', label: t('rewards.create.percentage'), icon: Percent },
                          { value: 'credit_points', label: t('rewards.create.creditPoints'), icon: Coins },
                          { value: 'free_months', label: t('rewards.create.freeMonths'), icon: CalendarDays },
                        ].map(({ value, label, icon: Icon }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              handleChange('discount_type', value);
                              if (value === 'free_months') {
                                handleChange('discount_value', formData.discount_value ? Math.max(1, Math.floor(formData.discount_value)) : null);
                              }
                            }}
                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs font-medium transition-colors ${
                              formData.discount_type === value
                                ? 'bg-orange-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            } ${value !== 'percentage' ? 'border-l border-gray-300' : ''}`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {formData.discount_type === 'percentage' ? t('rewards.create.discountValue') : formData.discount_type === 'credit_points' ? t('rewards.create.creditPointsLabel') : t('rewards.create.numberOfMonths')}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                          {formData.discount_type === 'percentage' ? '%' : formData.discount_type === 'credit_points' ? 'pts' : 'mo'}
                        </span>
                        <input
                          type="number"
                          value={formData.discount_value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              handleChange('discount_value', null);
                            } else {
                              const num = formData.discount_type === 'free_months'
                                ? Math.max(0, Math.floor(parseFloat(val) || 0))
                                : parseFloat(val);
                              handleChange('discount_value', num);
                            }
                          }}
                          className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="0"
                          min="0"
                          max={formData.discount_type === 'percentage' ? 100 : undefined}
                          step={formData.discount_type === 'free_months' ? 1 : undefined}
                        />
                      </div>
                    </div>

                    {products.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('rewards.create.productOptional')}</label>
                        <select
                          value={formData.discount_product_name}
                          onChange={(e) => handleChange('discount_product_name', e.target.value)}
                          className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        >
                          <option value="">{t('rewards.create.noSpecificProduct')}</option>
                          {products.map((product) => (
                            <option key={product.name} value={product.name}>{product.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formData.discount_value && (
                      <div className="bg-white rounded-lg p-3 border border-orange-200">
                        <span className="text-sm text-orange-800 font-medium">
                          {t('rewards.create.discountPreview', { label: buildDiscountName(formData.discount_type, formData.discount_value, formData.discount_product_name) })}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {formData.reward_method === 'BOTH' && (
                  <p className="text-xs text-gray-500 mt-3">
                    {t('rewards.create.bothHelp')}
                  </p>
                )}
              </div>
            )}

            {/* Approval & Limits */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ShieldCheck className="w-5 h-5 mr-2 text-blue-600" />
                {t('rewards.create.approvalLimits')}
              </h3>

              <div className="space-y-5">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="font-medium text-gray-900">{t('rewards.create.autoApproveLabel')}</label>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {formData.auto_approve
                        ? t('rewards.create.autoApproveOn')
                        : t('rewards.create.autoApproveOff')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.auto_approve}
                    onClick={() => handleChange('auto_approve', !formData.auto_approve)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      formData.auto_approve ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.auto_approve ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-900 flex items-center">
                        <RotateCcw className="w-4 h-4 mr-2 text-gray-500" />
                        {t('rewards.create.maxTimesLabel')}
                      </label>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {t('rewards.create.maxTimesHelp')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 mt-3">
                    <input
                      type="number"
                      value={formData.max_times}
                      onChange={(e) => {
                        handleChange('max_times', Math.max(1, parseInt(e.target.value) || 1));
                      }}
                      className="w-24 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      min="1"
                    />
                    <span className="text-sm text-gray-500">
                      {t('rewards.create.limitedToTimes', { count: formData.max_times })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Delete button — custom type only */}
            {isCustomType && onDelete && (
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={async () => {
                    if (!rewardType) return;
                    setIsDeleting(true);
                    try {
                      await onDelete(rewardType.id);
                      handleClose();
                    } catch (error) {
                      console.error('Failed to delete custom reward:', error);
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                  disabled={isDeleting}
                  className="w-full px-4 py-3 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors font-medium border border-red-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span>{isDeleting ? t('common.deleting') : t('rewards.edit.deleteCustomReward')}</span>
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium border-2 border-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !formData.name.trim() || !formData.description.trim()}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('common.saving')}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{t('common.saveChanges')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditRewardModal;
